import { memo, type ReactNode } from 'react';

import { PlayIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import {
  WORKSPACE_AGENT_PANEL_PILL_THUMBNAIL,
  type WorkspaceAgentPanelImageDimensions,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

export type WorkspaceAgentPillThumbnailLayout = 'fill' | 'pill';

export type WorkspaceAgentPillThumbnailProps = {
  dimensions?: WorkspaceAgentPanelImageDimensions;
  /** Drawn on the block's own ground when there is no still — the subject's glyph, not a blank tile. */
  fallback?: ReactNode;
  /** Timed media: without the glyph the still is indistinguishable from a photo at chip scale. */
  hasPlayOverlay?: boolean;
  /** `pill` is the 18px leading block inside a chip; `fill` sizes to the parent (list rows, peek stack). */
  layout?: WorkspaceAgentPillThumbnailLayout;
  testId?: string;
  url: string | null;
};

/**
 * A chip's leading still: full height and flush to the leading edge, so the chip reads as the subject
 * rather than a label with an icon pinned to it. The chip's own `overflow-hidden` rounds it.
 */
export const WorkspaceAgentPillThumbnail = memo(
  ({
    dimensions = WORKSPACE_AGENT_PANEL_PILL_THUMBNAIL,
    fallback,
    hasPlayOverlay = false,
    layout = 'pill',
    testId,
    url,
  }: WorkspaceAgentPillThumbnailProps) => {
    return (
      <span
        className={cn(
          'relative flex shrink-0 items-center justify-center ads:bg-subtle bg-gray-3 ads:text-icon-subtle text-gray-9',
          layout === 'pill' ? 'h-full w-[18px]' : 'size-full',
        )}
      >
        {url ? (
          <WorkspaceAgentPanelImage
            {...dimensions}
            alt=""
            className="size-full object-cover"
            data-testid={testId}
            src={url}
          />
        ) : (
          <span
            className="flex size-full items-center justify-center overflow-hidden"
            data-testid="WORKSPACE_AGENT_PILL_THUMBNAIL_FALLBACK"
          >
            {fallback}
          </span>
        )}
        {hasPlayOverlay ? (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/25"
            data-testid="WORKSPACE_AGENT_PILL_THUMBNAIL_PLAY"
          >
            <PlayIcon className="text-white" fill="currentColor" size={8} />
          </span>
        ) : null}
      </span>
    );
  },
);

WorkspaceAgentPillThumbnail.displayName = 'WorkspaceAgentPillThumbnail';
